import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Stack, Text } from "@mantine/core";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { WeeklyScheduleEditor, type OverrideValue, type SlotsByDay, type Weekday } from "@nodus/ui";
import { tenantApi, type WeeklyScheduleSlot } from "@/lib/api/client";

const WEEKDAY_KEYS: Weekday[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/** Group the wire-shape ``WeeklyScheduleSlot[]`` from
 *  ``GET /tenant-portal/weekly-schedule/`` into the per-weekday map
 *  that ``@nodus/ui`` ``WeeklyScheduleEditor`` consumes. Days with
 *  no slots get an empty array (so they appear as "Closed" in the
 *  preset chooser instead of "Unspecified"). */
function slotsToMap(slots: WeeklyScheduleSlot[]): SlotsByDay {
  const map: SlotsByDay = {};
  for (const k of WEEKDAY_KEYS) map[k] = [];
  for (const s of slots) {
    if (s.day_of_week < 0 || s.day_of_week > 6) continue;
    const key = WEEKDAY_KEYS[s.day_of_week];
    (map[key] = map[key] ?? []).push({
      start: s.start.slice(0, 5),
      end: s.end.slice(0, 5),
      available: s.available,
    });
  }
  return map;
}

/** Flatten the editor's ``SlotsByDay`` back to wire rows.
 *  Absent days (the ``Unspecified`` preset) emit no rows, matching
 *  the backend's whole-collection replace semantics. */
function mapToSlots(map: SlotsByDay): WeeklyScheduleSlot[] {
  const out: WeeklyScheduleSlot[] = [];
  WEEKDAY_KEYS.forEach((key, idx) => {
    const slots = map[key];
    if (!slots) return;
    for (const slot of slots) {
      out.push({
        day_of_week: idx,
        start: slot.start,
        end: slot.end,
        available: slot.available,
      });
    }
  });
  return out;
}

/** The company's weekly opening hours and the manual open/closed override —
 *  the same data the staff TenantForm edits. Used on Settings and in the
 *  Company setup guide (#436), where `onSaved` re-evaluates the step. */
export function OpeningHoursEditor({ onSaved }: { onSaved?: () => void } = {}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["weekly-schedule"],
    queryFn: () => tenantApi.getWeeklySchedule(),
  });

  const [value, setValue] = useState<SlotsByDay>({});
  const [override, setOverride] = useState<OverrideValue>(null);
  const [overrideUntil, setOverrideUntil] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!data?.data) return;
    setValue(slotsToMap(data.data.schedules));
    setOverride(data.data.opening_override);
    setOverrideUntil(data.data.opening_override_until);
    setDirty(false);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      tenantApi.updateWeeklySchedule({
        schedules: mapToSlots(value),
        opening_override: override ?? null,
      }),
    onSuccess: () => {
      toast.success("Opening hours saved");
      queryClient.invalidateQueries({ queryKey: ["weekly-schedule"] });
      setDirty(false);
      onSaved?.();
    },
    onError: () => toast.error("Failed to save opening hours"),
  });

  if (isLoading) return <Text fz="sm" c="dimmed">Loading...</Text>;

  return (
    <Stack gap="sm">
      <Card withBorder padding="lg">
        <WeeklyScheduleEditor
          value={value}
          onChange={(next) => {
            setValue(next);
            setDirty(true);
          }}
          overrideValue={override}
          onOverrideChange={(next) => {
            setOverride(next);
            setDirty(true);
          }}
          overrideUntil={overrideUntil}
          disabled={saveMutation.isPending}
        />
      </Card>

      {dirty && (
        <Button fullWidth onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          <Save size={16} style={{ marginRight: 8 }} />
          {saveMutation.isPending ? "Saving..." : "Save opening hours"}
        </Button>
      )}
    </Stack>
  );
}

export default OpeningHoursEditor;
